import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchFamilyPlan } from "../../../store/slices/tentativeFamilyPlanSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function PrintTentativeFamilyPlan() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentPlan, isLoading, error } = useSelector(
    (state) => state.tentativeFamilyPlan
  );

  useEffect(() => {
    if (id) dispatch(fetchFamilyPlan(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentPlan) {
      const timer = setTimeout(() => window.print(), 600);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentPlan]);

  if (isLoading) return <div className="p-3">Loading Family Plan...</div>;
  if (error) return <div className="p-3 text-danger">{error}</div>;
  if (!currentPlan) return <div className="p-3">No data found</div>;

  const d = currentPlan;

  const fmtDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    }) : "-";

  return (
    <>
      <style jsx global>{`
        * { box-sizing: border-box !important; }
        body { font-family: Arial, sans-serif !important; background: #fff !important; color: #000 !important; font-size: 12px !important; margin: 0 !important; padding: 0 !important; }
        
        .print-container { padding: 30px !important; position: relative !important; }
        ${printFooterStyles}
        .logo { position: absolute !important; top: 15px !important; left: 20px !important; }
        .logo img { height: 60px !important; width: auto !important; }

        .header { text-align: center !important; margin-bottom: 25px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
        .header h3 { font-size: 1.6rem !important; font-weight: bold !important; margin-bottom: 5px !important; }
        .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; }

        /* Unified Table Style */
        table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
        td, th { border: 1px solid #000 !important; padding: 8px 10px !important; vertical-align: top !important; }
        .label-bg { background: #f2f2f2 !important; font-weight: bold !important; font-size: 11px !important; }

        /* Detailed Text Section */
        .detail-section { margin-bottom: 15px !important; }
        .detail-label { background: #eee !important; font-weight: bold !important; border: 1px solid #000 !important; padding: 5px 10px !important; display: block !important; }
        .detail-content { border: 1px solid #000 !important; border-top: none !important; padding: 10px !important; min-height: 50px !important; line-height: 1.5 !important; }

        .footer { margin-top: 40px !important; border-top: 1.5px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 11px !important; }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
        <div className="logo"><img src={logo} alt="Logo" /></div>
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>TENTATIVE FAMILY PLAN</h6>
        </div>

        {/* Top Info Table - Single Row as requested */}
        <table>
          <tbody>
            <tr>
              <td className="label-bg">Client Name</td>
              <td className="label-bg">Date of Admission</td>
              <td className="label-bg">History Meeting</td>
              <td className="label-bg">First Call</td>
            </tr>
            <tr>
              <td><strong>{d.client_name || "N/A"}</strong></td>
              <td>{fmtDate(d.date_of_admission)}</td>
              <td>{fmtDate(d.history_taking_meeting)}</td>
              <td>{fmtDate(d.first_call_with_family)}</td>
            </tr>
          </tbody>
        </table>

        {/* Separated Text Area Sections */}
        <div className="detail-section">
          <span className="detail-label">Call Schedule:</span>
          <div className="detail-content">{d.call_schedule || "No details provided."}</div>
        </div>

        <div className="detail-section">
          <span className="detail-label">Monthly Family Meeting:</span>
          <div className="detail-content">{d.monthly_family_meeting || "No details provided."}</div>
        </div>

        <div className="detail-section">
          <span className="detail-label">Family and Client Meeting:</span>
          <div className="detail-content">{d.family_client_meeting || "No details provided."}</div>
        </div>

        <div className="detail-section">
          <span className="detail-label">Outings:</span>
          <div className="detail-content">{d.outings || "No details provided."}</div>
        </div>

        {/* Home Visit & Stays Table - 3 Rows as requested */}
        <table style={{ marginTop: '30px' }}>
          <thead>
            <tr>
              <th className="label-bg" style={{ width: '40%' }}>Activity Type</th>
              <th className="label-bg" style={{ width: '30%' }}>Status</th>
              <th className="label-bg" style={{ width: '30%' }}>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Home Visit</strong></td>
              <td>{d.home_visit}</td>
              <td>{fmtDate(d.home_visit_date)}</td>
            </tr>
            <tr>
              <td><strong>Night Stay</strong></td>
              <td>{d.night_stay}</td>
              <td>{fmtDate(d.night_stay_date)}</td>
            </tr>
            <tr>
              <td><strong>Weekend Stay</strong></td>
              <td>{d.weekend_stay}</td>
              <td>{fmtDate(d.weekend_stay_date)}</td>
            </tr>
          </tbody>
        </table>

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '50px' }}>
          <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>
            <strong>Psych Rehab Practitioner</strong>
          </div>
          <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>
            <strong>Family Signature</strong>
          </div>
        </div>

        <div className="footer">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph : 021-34546364-65-66</p>
          <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
        </div>
      </div>
      </PrintWrapper>
    </>
  );
}